import {
  Component,
  inject,
  OnInit,
  Renderer2,
  DOCUMENT,
  effect,
  PLATFORM_ID,
  makeStateKey,
  StateKey,
  TransferState,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  PendingTasks,
} from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { HeaderComponent } from './parts/header/header.component';
import { FooterComponent } from './parts/footer/footer.component';
import { AppService } from './services/app.service';
import { SettingsService } from './services/settings.service';

import { Settings } from './models/settings/setting.model';
import { Layout } from './models/layout/layout.model';
import { LayoutService } from './services/layout.service';
import { StyleService } from './services/style.service';
import { ProjectFontFamily } from './models/layout/project-font-families.model';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from './services/analytics.service';
import { CookieWrapperComponent } from './parts/components/cookie/cookie-wrapper/cookie-wrapper.component';
import { CookieConsentService } from './services/cookie-consent.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { BreakpointService } from './services/breakpoint.service';
import { Pill, Pills } from './models/pills.model';
import { Media } from './models/page-components/media.model';

type FaviconLinkAttributes = {
  rel: string;
  type?: string;
  sizes?: string;
};

type ApiFavicon = {
  Type?: string;
  Image?: Media;
  Url?: string;
};

const FAVICON_LINK_ATTRIBUTES: Record<string, FaviconLinkAttributes> = {
  'favicon.ico': {
    rel: 'shortcut icon',
  },
  'favicon-16x16.png': {
    rel: 'icon',
    type: 'image/png',
    sizes: '16x16',
  },
  'favicon-32x32.png': {
    rel: 'icon',
    type: 'image/png',
    sizes: '32x32',
  },
  'apple-touch-icon.png': {
    rel: 'apple-touch-icon',
    sizes: '180x180',
  },
  'android-chrome-192x192.png': {
    rel: 'icon',
    type: 'image/png',
    sizes: '192x192',
  },
  'android-chrome-512x512.png': {
    rel: 'icon',
    type: 'image/png',
    sizes: '512x512',
  },
  'favicon.svg': {
    rel: 'icon',
    type: 'image/svg+xml',
  },
};

const SETTINGS_STATE_KEY = makeStateKey<Settings>('app-data:settings');
const FONT_FAMILIES_STATE_KEY = makeStateKey<ProjectFontFamily[]>(
  'app-data:font-family',
);
const LAYOUT_STATE_KEY = makeStateKey<Layout>('app-data:layout');
const MAX_TRANSFER_STATE_PAYLOAD_BYTES = 100_000;

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    CookieWrapperComponent,
    CommonModule,
  ],
  templateUrl: './app.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  appService = inject(AppService);
  layoutService = inject(LayoutService);
  styleService = inject(StyleService);
  settingsService = inject(SettingsService);
  document = inject(DOCUMENT);
  renderer = inject(Renderer2);
  platformId = inject(PLATFORM_ID);
  analyticsService = inject(AnalyticsService);
  cookieConsentService = inject(CookieConsentService);
  breakpointService = inject(BreakpointService);
  transferState = inject(TransferState);
  cdr = inject(ChangeDetectorRef);
  pendingTasks = inject(PendingTasks);

  apiDomain = environment.apiDomain;
  settings: Settings = new Settings();
  layout?: Layout;
  fontFamilies: ProjectFontFamily[] = [];
  title = 'IcId';

  cssVariablesRoot: string[] = [];
  cssVariables: string[] = [];

  loadCookieConsentWrapper = false;

  constructor() {
    effect(() => {
      this.cookieConsentService.consent();
      this.setUpAnalytics();
    });

    effect(() => {
      this.settings = this.settingsService.setting() ?? new Settings();

      if (
        isPlatformBrowser(this.platformId) &&
        this.settings.Analytics?.TagManagerId
      ) {
        this.setUpAnalytics();
      }
    });
  }

  ngOnInit(): void {
    this.cookieConsentService.checkConsentFromStorage('app.component');

    this.pendingTasks.run(() => this.loadSettings());
    this.pendingTasks.run(() => this.loadFontFamilies());
    this.pendingTasks.run(() => this.loadLayout());
  }

  private async loadSettings(): Promise<void> {
    const transferredSettings = this.takeTransferState(SETTINGS_STATE_KEY);

    if (transferredSettings) {
      this.applySettings(transferredSettings);
      return;
    }

    const response = await this.appService.get('settings');
    const settings = JSON.parse(response) as Settings;

    this.setTransferState(SETTINGS_STATE_KEY, settings);
    this.applySettings(settings);
  }

  private async loadFontFamilies(): Promise<void> {
    const transferredFontFamilies = this.takeTransferState(
      FONT_FAMILIES_STATE_KEY,
    );

    if (transferredFontFamilies) {
      this.applyFontFamilies(transferredFontFamilies);
      return;
    }

    const response = await this.appService.get('font-family');
    const fontFamilies = JSON.parse(response) as ProjectFontFamily[];

    this.setTransferState(FONT_FAMILIES_STATE_KEY, fontFamilies);
    this.applyFontFamilies(fontFamilies);
  }

  private async loadLayout(): Promise<void> {
    const transferredLayout = this.takeTransferState(LAYOUT_STATE_KEY);

    if (transferredLayout) {
      this.applyLayout(transferredLayout);
      return;
    }

    const response = await this.appService.get('layout');
    const layout = JSON.parse(response) as Layout;

    this.setTransferState(LAYOUT_STATE_KEY, layout, {
      maxPayloadBytes: MAX_TRANSFER_STATE_PAYLOAD_BYTES,
    });
    this.applyLayout(layout);
  }

  private applySettings(settings: Settings): void {
    this.settings = settings;
    this.settingsService.updateSettings(this.settings);
    this.addOrganizationSchema();
    this.showCookieConsent();
    this.setSiteLanguage();
    this.cdr.markForCheck();
  }

  private applyFontFamilies(fontFamilies: ProjectFontFamily[]): void {
    this.fontFamilies = fontFamilies;
    this.insertCssFontFaces();
    this.cdr.markForCheck();
  }

  private applyLayout(layout: Layout): void {
    this.layout = layout;

    if (!this.layout) return;

    this.layoutService.updateLayout(this.layout);
    this.setFavicon();
    this.setCssVariables();
    this.loadCustomJS();
    this.loadCustomCSS();
    this.cdr.markForCheck();
  }

  private takeTransferState<T>(key: StateKey<T>): T | null {
    if (this.appService.isServer() || !this.transferState.hasKey(key)) {
      return null;
    }

    const value = this.transferState.get<T | null>(key, null);
    this.transferState.remove(key);
    return value;
  }

  private setTransferState<T>(
    key: StateKey<T>,
    value: T,
    options?: { maxPayloadBytes?: number },
  ): void {
    if (!this.appService.isServer()) {
      return;
    }

    if (
      options?.maxPayloadBytes !== undefined &&
      JSON.stringify(value).length > options.maxPayloadBytes
    ) {
      return;
    }

    this.transferState.set(key, value);
  }

  showCookieConsent() {
    if (isPlatformBrowser(this.platformId)) {
      this.loadCookieConsentWrapper =
        this.settings?.Privacy?.ShowCookieConsent ?? false;
    }
  }

  setFavicon() {
    const favicons = this.getFavicons();

    if (favicons.length === 0) {
      return;
    }

    this.document
      .querySelectorAll('link[data-api-favicon="true"]')
      .forEach((link) => link.remove());

    favicons.forEach((favicon) => {
      const faviconKey = this.getFaviconKey(favicon);
      const faviconUrl = this.getFaviconUrl(favicon);

      if (!faviconKey || !faviconUrl) {
        return;
      }

      const attributes = FAVICON_LINK_ATTRIBUTES[faviconKey];
      const link = this.document.createElement('link');
      link.rel = attributes.rel;
      link.href = this.apiDomain + 'upload/media/' + faviconUrl;
      link.setAttribute('data-api-favicon', 'true');

      if (attributes.type) {
        link.type = attributes.type;
      }

      if (attributes.sizes) {
        link.setAttribute('sizes', attributes.sizes);
      }

      this.document.head.appendChild(link);
    });
  }

  private getFavicons(): ApiFavicon[] {
    const logo = this.layout?.Logo as
      | {
          Favicon?: ApiFavicon[] | ApiFavicon | null;
          Favicons?: ApiFavicon[] | null;
        }
      | undefined;
    const favicons = logo?.Favicon;

    if (Array.isArray(favicons) && favicons.length > 0) {
      return favicons;
    }

    if (Array.isArray(logo?.Favicons) && logo.Favicons.length > 0) {
      return logo.Favicons;
    }

    return favicons && !Array.isArray(favicons) ? [favicons] : [];
  }

  private getFaviconKey(favicon: ApiFavicon): string | null {
    const type = favicon.Type?.length ? favicon.Type : undefined;
    const urlFileName = this.getFaviconUrl(favicon)
      ?.split(/[\\/]/)
      .pop()
      ?.split('?')[0];
    const faviconKey = (type ?? urlFileName)?.toLowerCase();

    return faviconKey && FAVICON_LINK_ATTRIBUTES[faviconKey]
      ? faviconKey
      : null;
  }

  private getFaviconUrl(favicon: ApiFavicon): string | undefined {
    return favicon.Image?.Url ?? favicon.Url;
  }

  setCssVariables() {
    if (!this.layout) return;

    // Create style element for CSS variables - works on both SSR and browser
    const styleElement = this.document.createElement('style');

    // Add root variables
    if (this.cssVariablesRoot.length > 0) {
      styleElement.textContent =
        ':root { ' + this.cssVariablesRoot.join(';') + ' }';
    }

    if (this.layout?.CssRootVariables.length > 0) {
      styleElement.textContent =
        ':root { ' + this.layout.CssRootVariables.join(';') + ' }';
    }

    // Add other CSS rules (like font-faces)
    if (this.cssVariables.length > 0) {
      styleElement.textContent += '\n' + this.cssVariables.join('\n');
    }

    this.document.head.appendChild(styleElement);
  }

  insertCssFontFaces() {
    if (!this.fontFamilies) return;

    const fontFaces: string[] = [];

    this.fontFamilies.forEach((font) => {
      font.Links.forEach((link) => {
        const fontFace = `@font-face {
        font-family: '${font.Family}';
        font-display: swap;
        src: url('/assets/fonts/${link.Url}') format('truetype');
        font-weight: ${link.Weight};
        font-style: ${link.FontStyle ?? 'normal'};
        }`;

        fontFaces.push(fontFace);
      });
    });

    this.document
      .querySelectorAll('style[data-api-font-faces="true"]')
      .forEach((style) => style.remove());

    const styleElement = this.document.createElement('style');
    styleElement.setAttribute('data-api-font-faces', 'true');
    styleElement.textContent = fontFaces.join('\n');
    this.document.head.appendChild(styleElement);
  }

  setPillStyles(pills: Pills | undefined): string {
    if (!pills || !pills.Pills || pills.Pills.length === 0) return '';

    let styleString = '';
    pills.Pills.forEach((pill) => {
      styleString += pill.CssClass;
    });

    return styleString;
  }

  loadCustomJS() {
    if (!this.layout?.CustomJS || !isPlatformBrowser(this.platformId)) return;

    this.runWhenIdle(() => {
      const script = this.document.createElement('script');

      script.setAttribute('async', '');
      script.setAttribute('defer', '');
      script.innerHTML = this.layout?.CustomJS ?? '';

      const headElement = this.document.querySelector('head');
      headElement?.appendChild(script);
    });
  }

  loadCustomCSS() {
    if (!this.layout?.CustomCSS) return;
    const styleSheet = this.document.createElement('style');

    styleSheet.innerHTML =
      (this.layout?.CustomCSS ?? '') +
      (this.setPillStyles(this.layout?.Pills) ?? '');

    this.document.head.appendChild(styleSheet);
  }

  private runWhenIdle(callback: () => void): void {
    const scheduleIdle = () => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(callback, { timeout: 3000 });
        return;
      }

      window.setTimeout(callback, 1000);
    };

    if (document.readyState === 'complete') {
      scheduleIdle();
      return;
    }

    window.addEventListener('load', scheduleIdle, { once: true });
  }

  private addOrganizationSchema() {
    if (!this.settings?.Organization) return;

    if (!this.appService.isServer()) return;

    const organization = this.settings.Organization;
    if (!organization || !organization.Name) {
      return;
    }

    const schema: { [key: string]: any } = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: organization.Name,
      description: organization.Description,
      url: organization.Url,
      logo: organization.Logo?.Url || '',
      email: organization.Email,
      telephone: organization.PhoneNumber,
      address: {
        '@type': 'PostalAddress',
        streetAddress: organization.StreetAddress,
        addressLocality: organization.AddressLocality,
        addressRegion: organization.AddressRegion,
        postalCode: organization.PostalCode,
        addressCountry: organization.AddressCountry,
      },
    };

    // Remove empty properties
    Object.keys(schema).forEach((key) => {
      const typedKey = key as keyof typeof schema;
      if (
        schema[typedKey] === '' ||
        schema[typedKey] === null ||
        schema[typedKey] === undefined
      ) {
        delete schema[typedKey];
      }
    });

    // Remove empty address properties
    if (schema['address']) {
      Object.keys(schema['address']).forEach((key) => {
        const typedKey = key as keyof (typeof schema)['address'];
        if (
          schema['address'][typedKey] === '' ||
          schema['address'][typedKey] === null ||
          schema['address'][typedKey] === undefined
        ) {
          delete schema['address'][typedKey];
        }
      });

      // Remove address object if it only has @type
      if (Object.keys(schema['address']).length <= 1) {
        delete schema['address'];
      }
    }

    const script = this.renderer.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    this.renderer.appendChild(this.document.head, script);
  }

  setUpAnalytics() {
    if (
      isPlatformBrowser(this.platformId) &&
      this.settings?.Analytics?.TagManagerId &&
      !this.cookieConsentService.tagManagerLoaded
    ) {
      this.cookieConsentService.loadTagManagerScript(
        this.settings.Analytics.TagManagerId,
        this.renderer,
      );
    }

    if (
      isPlatformBrowser(this.platformId) &&
      this.settings?.Analytics?.TagManagerId &&
      this.cookieConsentService.tagManagerLoaded &&
      this.cookieConsentService.consent().Version > 0
    ) {
      this.cookieConsentService.syncConsentWithTagManager(
        this.cookieConsentService.consent(),
      );
    }
  }

  setSiteLanguage() {
    if (this.settings?.Language) {
      switch (this.settings.Language) {
        case 'se-SV':
          this.document.documentElement.lang = 'sv';
          break;
        case 'en-US':
          this.document.documentElement.lang = 'en';
          break;
        case 'nb-NO':
          this.document.documentElement.lang = 'no';
          break;
        default:
          this.document.documentElement.lang = 'en';
      }
    }
  }
}
